import { z } from 'zod';
import { apenasDigitos } from './documento.ts';
import { aliquotaOpcionalSchema, codigoFiscalOpcional } from './fiscal.ts';

/**
 * Os dados com que a empresa emite nota fiscal de serviço (`.spec/015`,
 * decisão 88).
 *
 * Tabela própria (`empresas_fiscal`, uma linha por empresa), e não colunas de
 * `pessoas`, pelo mesmo motivo do logotipo: `pessoas` é a tabela mais lida do
 * sistema, e onze colunas que só a emissão consulta seriam arrastadas por toda
 * leitura de cliente e de fornecedor. O que é de **toda** pessoa — e-mail,
 * telefone, inscrição municipal — ficou lá; o que é de quem **emite** ficou
 * aqui.
 *
 * ## Nenhum campo é obrigatório
 *
 * A empresa se cadastra no dia em que entra no sistema, e pode nunca emitir
 * nota por ele. Quem cobra o que falta é a emissão, pelo motor configurado na
 * conta (`motores-nfse.ts`) — cada motor sabe o que a prefeitura dele exige, e
 * o cadastro não tem como adivinhar. Um `CHECK` de preenchimento aqui recusaria
 * todas as empresas gravadas antes da tabela existir.
 *
 * ## Nada daqui é de um motor específico
 *
 * São dados **do contribuinte**: valem para qualquer emissor de NFS-e no padrão
 * nacional. O que é do motor — chave de API, identificador do emitente lá,
 * certificado digital — não está neste contrato, e é a fase 2: aquilo muda
 * quando se troca de motor, isto não.
 */

/**
 * Onde a nota é emitida.
 *
 * `HOMOLOGACAO` é o padrão, e é a escolha segura: a nota emitida em produção
 * por engano tem valor fiscal e só se desfaz cancelando na prefeitura; a
 * emitida em homologação por engano não vale nada, e basta emitir de novo.
 * Passar a `PRODUCAO` é um gesto deliberado, e fica no log de alterações.
 *
 * O motor Manual ignora o campo — quem emite fora do sistema já escolheu o
 * ambiente lá.
 */
export const AMBIENTES_NFSE = ['HOMOLOGACAO', 'PRODUCAO'] as const;
export const ambienteNfseSchema = z.enum(AMBIENTES_NFSE);
export type AmbienteNfse = z.infer<typeof ambienteNfseSchema>;

export const ROTULO_AMBIENTE_NFSE: Readonly<Record<AmbienteNfse, string>> = {
  HOMOLOGACAO: 'Homologação (teste)',
  PRODUCAO: 'Produção',
};

export const DICA_AMBIENTE_NFSE: Readonly<Record<AmbienteNfse, string>> = {
  HOMOLOGACAO: 'A nota sai sem valor fiscal. É o ambiente para testar a emissão.',
  PRODUCAO: 'A nota tem valor fiscal, e só se desfaz cancelando na prefeitura.',
};

/** Série da NFS-e: até 5 dígitos. O padrão nacional numera por série. */
export const TAMANHO_MAXIMO_SERIE_NFSE = 5;

export const fiscalDaEmpresaSchema = z.object({
  ambienteNfse: ambienteNfseSchema.prefault('HOMOLOGACAO'),

  serieNfse: z
    .string()
    .transform(apenasDigitos)
    .refine(
      (valor) => valor.length <= TAMANHO_MAXIMO_SERIE_NFSE,
      `A série tem no máximo ${TAMANHO_MAXIMO_SERIE_NFSE} dígitos.`,
    )
    .transform((valor) => (valor.length === 0 ? null : valor))
    .nullable()
    .prefault(null),

  /** CNAE principal — sete dígitos. Alguns municípios o exigem na nota. */
  cnae: codigoFiscalOpcional(7, 'O CNAE'),

  /**
   * Simples Nacional que **passou do sublimite** de receita.
   *
   * Não é um quarto regime: a empresa continua no Simples para os tributos
   * federais e passa a recolher ISS e ICMS por fora, como o regime normal. Por
   * isso é marca ao lado, e não mais um valor de `regimeTributario` — aquele
   * decide crédito de imposto na compra (`.spec/001`, seção 2.2), e um quarto
   * código lá obrigaria todo `= 'SIMPLES'` do sistema a virar `IN (…)`.
   *
   * Só tem sentido com regime `SIMPLES`; nos demais é inerte, e não há recusa:
   * trocar o regime da empresa não deve devolver erro preso a um campo de
   * outro bloco da tela.
   */
  simplesExcessoSublimite: z.boolean().prefault(false),

  /** Alíquota efetiva do Simples (%), a do último DAS. Vai impressa na nota do optante. */
  aliquotaSimples: aliquotaOpcionalSchema,

  /** CST de PIS/COFINS padrão — dois dígitos (`01`, `06`, `07`…). Regime normal. */
  cstPisCofins: codigoFiscalOpcional(2, 'O CST de PIS/COFINS'),
  aliquotaPis: aliquotaOpcionalSchema,
  aliquotaCofins: aliquotaOpcionalSchema,

  /*
    Reforma tributária (LC 214/2025): os três códigos do grupo IBS/CBS da nota.
    Padrões da empresa — a nota os copia e pode trocá-los.
  */
  /** CST de IBS/CBS — três dígitos. */
  cstIbsCbs: codigoFiscalOpcional(3, 'O CST de IBS/CBS'),
  /** Classificação tributária (`cClassTrib`) — seis dígitos. */
  classificacaoTributaria: codigoFiscalOpcional(6, 'A classificação tributária'),
  /** Indicador de operação (`cIndOp`) — seis dígitos. */
  indicadorOperacao: codigoFiscalOpcional(6, 'O indicador de operação'),
});

/** O bloco como entra no payload — tudo opcional. */
export type FiscalDaEmpresaEntrada = z.input<typeof fiscalDaEmpresaSchema>;
/** O bloco depois de validado — é o que o service grava. */
export type FiscalDaEmpresa = z.infer<typeof fiscalDaEmpresaSchema>;

/** A leitura: o que o banco guarda, sem transformação. */
export const fiscalDaEmpresaRespostaSchema = z.object({
  ambienteNfse: ambienteNfseSchema,
  serieNfse: z.string().nullable(),
  cnae: z.string().nullable(),
  simplesExcessoSublimite: z.boolean(),
  aliquotaSimples: z.string().nullable(),
  cstPisCofins: z.string().nullable(),
  aliquotaPis: z.string().nullable(),
  aliquotaCofins: z.string().nullable(),
  cstIbsCbs: z.string().nullable(),
  classificacaoTributaria: z.string().nullable(),
  indicadorOperacao: z.string().nullable(),
});

/**
 * O bloco de quem nunca preencheu nada.
 *
 * É o que a leitura devolve quando a empresa não tem linha em
 * `empresas_fiscal`, e o que a listagem devolve sempre. Uma constante, e não o
 * `parse({})` do schema a cada linha: o resultado é o mesmo, e a listagem não
 * precisa rodar onze transformações por empresa para chegar nele.
 */
export const FISCAL_DA_EMPRESA_VAZIO: FiscalDaEmpresa = {
  ambienteNfse: 'HOMOLOGACAO',
  serieNfse: null,
  cnae: null,
  simplesExcessoSublimite: false,
  aliquotaSimples: null,
  cstPisCofins: null,
  aliquotaPis: null,
  aliquotaCofins: null,
  cstIbsCbs: null,
  classificacaoTributaria: null,
  indicadorOperacao: null,
};
